/**
 * Convierte el primer nombre de familia desde un valor API o CSS a un nombre legible
 * (p. ej. "PlayfairDisplay" → "Playfair Display", "Poppins" → "Poppins").
 */
const COMPACT_NAME_MAP = {
	SFProDisplay: "SF Pro Display",
	SFProText: "SF Pro Text",
	DMSerifDisplay: "DM Serif Display",
};

export function expandCompactFontName(str) {
	if (!str || typeof str !== "string") return "";
	let s = str.split(",")[0].trim();
	s = s.replace(/^["']+|["']+$/g, "");
	if (s.includes(" ")) return s;
	if (COMPACT_NAME_MAP[s]) return COMPACT_NAME_MAP[s];
	if (!/[a-z][A-Z]/.test(s)) return s;
	return s.replace(/([a-z])([A-Z])/g, "$1 $2");
}

/**
 * Stack CSS listo para `font-family` a partir de lo guardado (compacto o con comas).
 */
export function fontFamilyToCssStack(str) {
	if (!str || typeof str !== "string") return str;
	const name = expandCompactFontName(str);
	if (!name) return str;
	if (/^SF Pro/i.test(name)) {
		return `'${name}', -apple-system, BlinkMacSystemFont, 'Inter', system-ui, sans-serif`;
	}
	const serif = /^(Playfair|Lora|Cormorant|Merriweather|DM Serif)\b/i.test(name);
	return `'${name}', ${serif ? "serif" : "sans-serif"}`;
}

const SYSTEM_FONTS = new Set([
	"serif",
	"sans-serif",
	"monospace",
	"cursive",
	"fantasy",
	"system-ui",
	"ui-sans-serif",
	"ui-serif",
	"ui-monospace",
	"georgia",
	"times new roman",
	"helvetica",
	"helvetica neue",
	"arial",
	"impact",
]);

const LOCAL_ONLY_FONTS = {
	"SF Pro Display": `
@font-face{font-family:"SF Pro Display";font-style:normal;font-weight:300;src:local("SF Pro Display Light"),url("/fonts/sf-pro/sf-pro-display-light.woff2") format("woff2");font-display:swap}
@font-face{font-family:"SF Pro Display";font-style:normal;font-weight:400;src:local("SF Pro Display"),url("/fonts/sf-pro/sf-pro-display-regular.woff2") format("woff2");font-display:swap}
@font-face{font-family:"SF Pro Display";font-style:normal;font-weight:500;src:local("SF Pro Display Medium"),url("/fonts/sf-pro/sf-pro-display-medium.woff2") format("woff2");font-display:swap}
@font-face{font-family:"SF Pro Display";font-style:normal;font-weight:600;src:local("SF Pro Display Semibold"),url("/fonts/sf-pro/sf-pro-display-semibold.woff2") format("woff2");font-display:swap}
@font-face{font-family:"SF Pro Display";font-style:normal;font-weight:700;src:local("SF Pro Display Bold"),url("/fonts/sf-pro/sf-pro-display-bold.woff2") format("woff2");font-display:swap}`,
};

/** Google Fonts URLs with weights/italics matching former index.css coverage. */
const GOOGLE_FONT_URLS = {
	"Archivo": "https://fonts.googleapis.com/css2?family=Archivo:wght@400;500;600;700&display=swap",
	"Arimo": "https://fonts.googleapis.com/css2?family=Arimo:wght@400;500;600;700&display=swap",
	"Barlow": "https://fonts.googleapis.com/css2?family=Barlow:wght@400;500;600;700&display=swap",
	"Bebas Neue": "https://fonts.googleapis.com/css2?family=Bebas+Neue&display=swap",
	"Cormorant Garamond":
		"https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;0,600;0,700;1,400&display=swap",
	"Cormorant SC":
		"https://fonts.googleapis.com/css2?family=Cormorant+SC:wght@300;400;500;600;700&display=swap",
	"DM Serif Display":
		"https://fonts.googleapis.com/css2?family=DM+Serif+Display:ital@0;1&display=swap",
	"Dosis": "https://fonts.googleapis.com/css2?family=Dosis:wght@400;500;600;700&display=swap",
	"Heebo": "https://fonts.googleapis.com/css2?family=Heebo:wght@400;500;600;700&display=swap",
	"Inconsolata": "https://fonts.googleapis.com/css2?family=Inconsolata:wght@400;500;600;700&display=swap",
	"Inter": "https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap",
	"Josefin Sans": "https://fonts.googleapis.com/css2?family=Josefin+Sans:wght@400;500;600;700&display=swap",
	"Jost": "https://fonts.googleapis.com/css2?family=Jost:wght@200;300;400;500;600;700&display=swap",
	"Kanit": "https://fonts.googleapis.com/css2?family=Kanit:wght@400;500;600;700&display=swap",
	"Karla": "https://fonts.googleapis.com/css2?family=Karla:wght@400;500;600;700&display=swap",
	"Lato": "https://fonts.googleapis.com/css2?family=Lato:wght@400;700&display=swap",
	"Lora": "https://fonts.googleapis.com/css2?family=Lora:wght@400;500;600;700&display=swap",
	"Manrope": "https://fonts.googleapis.com/css2?family=Manrope:wght@400;500;600;700&display=swap",
	"Merriweather Sans":
		"https://fonts.googleapis.com/css2?family=Merriweather+Sans:wght@400;500;600;700&display=swap",
	"Merriweather": "https://fonts.googleapis.com/css2?family=Merriweather:wght@400;700&display=swap",
	"Montserrat":
		"https://fonts.googleapis.com/css2?family=Montserrat:wght@100;200;300;400;500;600;700;900&display=swap",
	"Mukta": "https://fonts.googleapis.com/css2?family=Mukta:wght@400;500;600;700&display=swap",
	"Mulish": "https://fonts.googleapis.com/css2?family=Mulish:wght@400;500;600;700&display=swap",
	"Nunito": "https://fonts.googleapis.com/css2?family=Nunito:wght@400;500;600;700&display=swap",
	"Oswald": "https://fonts.googleapis.com/css2?family=Oswald:wght@400;500;600;700&display=swap",
	"Outfit": "https://fonts.googleapis.com/css2?family=Outfit:wght@400;500;600;700&display=swap",
	"Oxygen": "https://fonts.googleapis.com/css2?family=Oxygen:wght@400;700&display=swap",
	"Playfair Display":
		"https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,500;0,600;0,700;1,400&display=swap",
	"Plus Jakarta Sans":
		"https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700&display=swap",
	"Poppins": "https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700&display=swap",
	"Quicksand": "https://fonts.googleapis.com/css2?family=Quicksand:wght@400;500;600;700&display=swap",
	"Raleway": "https://fonts.googleapis.com/css2?family=Raleway:wght@400;500;600;700&display=swap",
	"Roboto": "https://fonts.googleapis.com/css2?family=Roboto:wght@400;500;700&display=swap",
	"Rubik": "https://fonts.googleapis.com/css2?family=Rubik:wght@400;500;600;700&display=swap",
	"Sora": "https://fonts.googleapis.com/css2?family=Sora:wght@400;500;600;700&display=swap",
	"Space Grotesk":
		"https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@400;500;600;700&display=swap",
	"Ubuntu": "https://fonts.googleapis.com/css2?family=Ubuntu:wght@400;500;700&display=swap",
	"Urbanist": "https://fonts.googleapis.com/css2?family=Urbanist:wght@400;500;600;700&display=swap",
};

function buildFontUrl(fontName) {
	const known = GOOGLE_FONT_URLS[fontName];
	if (known) return known;
	const formatted = fontName.replace(/\s+/g, "+");
	return `https://fonts.googleapis.com/css2?family=${formatted}:wght@400;500;600;700&display=swap`;
}

function injectLocalFontStyle(fontName, css) {
	const key = fontName.replace(/\s+/g, "-");
	if (document.querySelector(`style[data-local-font="${key}"]`)) return;
	const style = document.createElement("style");
	style.setAttribute("data-local-font", key);
	style.textContent = css;
	document.head.appendChild(style);
}

function injectFontLink(fontName) {
	const googleName = expandCompactFontName(fontName);
	if (!googleName) return;
	if (SYSTEM_FONTS.has(googleName.toLowerCase())) return;

	if (LOCAL_ONLY_FONTS[googleName]) {
		injectLocalFontStyle(googleName, LOCAL_ONLY_FONTS[googleName]);
		return;
	}

	const key = googleName.replace(/\s+/g, "+");
	if (document.querySelector(`link[data-font="${key}"]`)) return;

	const link = document.createElement("link");
	link.href = buildFontUrl(googleName);
	link.rel = "stylesheet";
	link.crossOrigin = "anonymous";
	link.setAttribute("data-font", key);
	document.head.appendChild(link);
}

/** Carga una fuente de Google Fonts por nombre o stack CSS. */
export const loadFont = (fontName) => {
	if (!fontName) return;
	injectFontLink(expandCompactFontName(fontName));
};

/** Carga varias fuentes (p. ej. principal del negocio + secundarias del template). */
export const loadFonts = (...fontNames) => {
	fontNames.flat().forEach((name) => loadFont(name));
};

/**
 * Fuentes secundarias por template (además de webTemplate.fontFamily, que carga WebTemplateContext).
 * Mismo criterio que barber8/barber2: una fuente principal vía API y extras solo donde el CSS las pide.
 */
export const TEMPLATE_EXTRA_FONTS = {
	restaurant19: ["Poppins"],
	restaurant20: ["Poppins", "Playfair Display"],
	barber6: ["Playfair Display"],
	barberiamarco: ["Inter", "DM Serif Display"],
	barberiamarco2: ["Inter", "DM Serif Display"],
	burgerbar: ["Inter", "Bebas Neue"],
	grillhouse: ["Inter", "Oswald"],
	brunch: ["Inter", "DM Serif Display"],
	riquino: ["Inter", "DM Serif Display", "Playfair Display"],
	flora: ["SF Pro Display", "Inter", "DM Serif Display"],
	rovica: ["Inter", "Playfair Display"],
	parcelona: ["Poppins", "Playfair Display"],
	shop6: ["Playfair Display"],
	hairdresser1: ["Cormorant Garamond"],
	hairdresser2: ["Cormorant Garamond", "Inter"],
	muse: ["Jost"],
	essenza: ["Cormorant Garamond", "Figtree"],
	gaps: ["Cormorant Garamond", "Figtree"],
};
